import { FaIcon, FaIconProps } from '@acp/core-ui';
import {
    Box,
    Container,
    Heading,
    HStack,
    Popover,
    PopoverContent,
    PopoverTrigger,
    useBreakpointValue,
    VStack,
} from '@chakra-ui/react';
import { ComponentProps } from 'react';

export function CardLayout({
    children,
    elevated = false,
    ...props
}: {
    children: React.ReactNode;
    elevated?: boolean;
} & ComponentProps<typeof VStack>) {
    return (
        <Container
            as={VStack}
            maxW="80em"
            alignItems="start"
            py={8}
            px={[5, 6, 8]}
            gap={6}
            shadow={elevated ? 'sm' : 'none'}
            {...props}
        >
            {children}
        </Container>
    );
}

function Title({
    children,
    ...props
}: {
    children?: React.ReactNode;
} & ComponentProps<typeof HStack>) {
    return (
        <HStack justifyContent="space-between" w="100%" alignItems="start" gap={2} {...props}>
            {children}
        </HStack>
    );
}

function TitleHeading({
    heading,
    children,
    ...props
}: {
    heading: string;
    children?: React.ReactNode;
} & ComponentProps<typeof VStack>) {
    return (
        <VStack w="full" alignItems="start" gap={0} {...props}>
            <Heading size="md">
                {heading}
            </Heading>
            {children}
        </VStack>
    );
}

function TitleDescription({
    children,
    ...props
}: {
    children?: React.ReactNode;
} & ComponentProps<typeof Box>) {
    return (
        <Box
            alignSelf="start"
            color="muted.500"
            {...props}
        >
            {children}
        </Box>
    );
}

function TitlePopover({
    popoverIcon,
    children,
}: {
    popoverIcon: FaIconProps['icon'];
    children?: React.ReactNode;
} & ComponentProps<typeof PopoverContent>) {
    const popoverWidth = useBreakpointValue({
        md: 450,
        lg: 500,
        xl: 560,
    });

    return (
        <Popover isLazy trigger="hover" placement="bottom-end">
            <PopoverTrigger>
                <FaIcon size="lg" icon={popoverIcon} color="muted.500" />
            </PopoverTrigger>
            <PopoverContent width={popoverWidth}>
                {children}
            </PopoverContent>
        </Popover>
    );
}

function Body({
    children,
    ...props
}: {
    children?: React.ReactNode;
} & ComponentProps<typeof VStack>) {
    return (
        <VStack w="full" {...props}>
            {children}
        </VStack>
    );
}

CardLayout.Title = Title;
CardLayout.Body = Body;

Title.Heading = TitleHeading;
Title.Description = TitleDescription;
Title.Popover = TitlePopover;
